/**
 * Coffee Bean Clicker v2 - Settings Panel
 * BGM/SFX/Vibration toggles, language selection, reset
 */

import * as PIXI from 'pixi.js';
import type { GameCore } from '@app-games/core';
import type { GameState, GameAction } from '../../game/index.js';
import { t } from '../../i18n/index.js';
import { setLanguage, getAvailableLanguages } from '../../i18n/index.js';
import { THEME, LAYOUT } from './theme.js';
import { drawGlassCard, drawPillButton, makeText } from './UIHelpers.js';

const LANGUAGE_LABELS: Record<string, string> = {
  ko: '\uD55C\uAD6D\uC5B4',
  en: 'English',
  ja: '\u65E5\u672C\u8A9E',
  zh: '\u4E2D\u6587',
  es: 'Espa\u00F1ol',
  de: 'Deutsch',
  fr: 'Fran\u00E7ais',
  pt: 'Portugu\u00EAs',
  th: '\u0E44\u0E17\u0E22',
};

interface ToggleRow {
  container: PIXI.Container;
  labelText: PIXI.Text;
  btnGfx: PIXI.Graphics;
  btnText: PIXI.Text;
  key: 'bgmEnabled' | 'sfxEnabled' | 'vibrationEnabled';
}

interface LangChip {
  gfx: PIXI.Graphics;
  text: PIXI.Text;
  lang: string;
}

export class SettingsPanel extends PIXI.Container {
  private core: GameCore<GameState, GameAction>;
  private panelW: number;
  private panelH: number;

  private toggleRows: ToggleRow[] = [];
  private langLabel!: PIXI.Text;
  private langChips: LangChip[] = [];
  private langContainer = new PIXI.Container();

  private resetGfx = new PIXI.Graphics();
  private resetText!: PIXI.Text;

  private versionText!: PIXI.Text;

  // Confirm state
  private confirmOverlay = new PIXI.Container();

  constructor(w: number, h: number, core: GameCore<GameState, GameAction>) {
    super();
    this.core = core;
    this.panelW = w;
    this.panelH = h;

    this.buildToggles();
    this.buildLanguageSelector();
    this.buildResetButton();
    this.buildVersion();
    this.buildConfirmOverlay();
  }

  private buildToggles(): void {
    const toggleDefs: { key: ToggleRow['key']; labelKey: string }[] = [
      { key: 'bgmEnabled', labelKey: 'settings.bgm' },
      { key: 'sfxEnabled', labelKey: 'settings.sfx' },
      { key: 'vibrationEnabled', labelKey: 'settings.vibration' },
    ];

    for (const def of toggleDefs) {
      const container = new PIXI.Container();

      const labelText = makeText(t(def.labelKey), 14, THEME.text.primary);
      labelText.x = LAYOUT.padding + 12;
      container.addChild(labelText);

      const btnGfx = new PIXI.Graphics();
      btnGfx.eventMode = 'static';
      btnGfx.cursor = 'pointer';
      btnGfx.on('pointerdown', () => this.toggleSetting(def.key));
      container.addChild(btnGfx);

      const btnText = makeText('', 12, THEME.text.white, { fontWeight: 'bold' });
      btnText.anchor.set(0.5, 0.5);
      container.addChild(btnText);

      this.addChild(container);
      this.toggleRows.push({ container, labelText, btnGfx, btnText, key: def.key });
    }
  }

  private toggleSetting(key: ToggleRow['key']): void {
    const state = this.core.getState();
    this.core.dispatch({
      type: 'UPDATE_SETTINGS',
      payload: { [key]: !state.settings[key] },
    });
  }

  private buildLanguageSelector(): void {
    this.langLabel = makeText(t('settings.language'), 14, THEME.text.primary);
    this.langLabel.x = LAYOUT.padding + 12;
    this.addChild(this.langLabel);

    this.addChild(this.langContainer);

    const langs = getAvailableLanguages();
    for (const lang of langs) {
      const gfx = new PIXI.Graphics();
      gfx.eventMode = 'static';
      gfx.cursor = 'pointer';
      gfx.on('pointerdown', () => this.selectLanguage(lang));
      this.langContainer.addChild(gfx);

      const text = makeText(LANGUAGE_LABELS[lang] || lang, 11, THEME.text.primary);
      text.anchor.set(0.5, 0.5);
      this.langContainer.addChild(text);

      this.langChips.push({ gfx, text, lang });
    }
  }

  private selectLanguage(lang: string): void {
    setLanguage(lang);
    this.core.dispatch({
      type: 'UPDATE_SETTINGS',
      payload: { language: lang },
    });
  }

  private buildResetButton(): void {
    this.resetGfx.eventMode = 'static';
    this.resetGfx.cursor = 'pointer';
    this.resetGfx.on('pointerdown', () => this.showConfirm());
    this.addChild(this.resetGfx);

    this.resetText = makeText(t('settings.reset'), 14, THEME.text.white, { fontWeight: 'bold' });
    this.resetText.anchor.set(0.5, 0.5);
    this.addChild(this.resetText);
  }

  private buildVersion(): void {
    this.versionText = makeText(`${t('settings.version')} 2.0.0`, 11, THEME.text.secondary);
    this.versionText.anchor.set(0.5, 0);
    this.addChild(this.versionText);
  }

  private buildConfirmOverlay(): void {
    this.confirmOverlay.visible = false;
    this.addChild(this.confirmOverlay);
  }

  private showConfirm(): void {
    this.confirmOverlay.removeChildren();

    // Dim bg
    const dim = new PIXI.Graphics();
    dim.beginFill(0x000000, 0.6);
    dim.drawRect(0, -this.y, this.panelW, this.panelH + this.y);
    dim.endFill();
    dim.eventMode = 'static';
    this.confirmOverlay.addChild(dim);

    const cardW = Math.min(this.panelW - 40, 280);
    const cardH = 140;
    const cx = (this.panelW - cardW) / 2;
    const cy = this.panelH * 0.3;

    const cardGfx = new PIXI.Graphics();
    drawGlassCard(cardGfx, cx, cy, cardW, cardH, LAYOUT.cardRadius, 0.2);
    this.confirmOverlay.addChild(cardGfx);

    const msgText = makeText(t('settings.resetConfirm'), 13, THEME.text.primary, {
      wordWrap: true,
      wordWrapWidth: cardW - 32,
    });
    msgText.anchor.set(0.5, 0);
    msgText.x = this.panelW / 2;
    msgText.y = cy + 20;
    this.confirmOverlay.addChild(msgText);

    // Confirm button (red)
    const confirmBtnW = 100;
    const confirmBtnH = 34;
    const confirmBtnX = this.panelW / 2 - confirmBtnW - 8;
    const confirmBtnY = cy + cardH - 50;

    const confirmBtnGfx = new PIXI.Graphics();
    drawPillButton(confirmBtnGfx, confirmBtnX, confirmBtnY, confirmBtnW, confirmBtnH, THEME.button.danger);
    confirmBtnGfx.eventMode = 'static';
    confirmBtnGfx.cursor = 'pointer';
    confirmBtnGfx.on('pointerdown', () => {
      this.core.reset();
      this.confirmOverlay.visible = false;
    });
    this.confirmOverlay.addChild(confirmBtnGfx);

    const confirmBtnLabel = makeText(t('settings.reset'), 12, THEME.text.white, { fontWeight: 'bold' });
    confirmBtnLabel.anchor.set(0.5, 0.5);
    confirmBtnLabel.x = confirmBtnX + confirmBtnW / 2;
    confirmBtnLabel.y = confirmBtnY + confirmBtnH / 2;
    this.confirmOverlay.addChild(confirmBtnLabel);

    // Cancel button
    const cancelBtnX = this.panelW / 2 + 8;
    const cancelBtnGfx = new PIXI.Graphics();
    drawPillButton(cancelBtnGfx, cancelBtnX, confirmBtnY, confirmBtnW, confirmBtnH, THEME.button.disabled);
    cancelBtnGfx.eventMode = 'static';
    cancelBtnGfx.cursor = 'pointer';
    cancelBtnGfx.on('pointerdown', () => {
      this.confirmOverlay.visible = false;
    });
    this.confirmOverlay.addChild(cancelBtnGfx);

    const cancelLabel = makeText('Cancel', 12, THEME.text.white, { fontWeight: 'bold' });
    cancelLabel.anchor.set(0.5, 0.5);
    cancelLabel.x = cancelBtnX + confirmBtnW / 2;
    cancelLabel.y = confirmBtnY + confirmBtnH / 2;
    this.confirmOverlay.addChild(cancelLabel);

    this.confirmOverlay.visible = true;
  }

  refresh(state: GameState): void {
    const pad = LAYOUT.padding;
    const rowW = this.panelW - pad * 2;
    const toggleBtnW = 60;
    const toggleBtnH = 30;
    let y = pad + 8;

    // ── Toggle rows ──
    for (const row of this.toggleRows) {
      const rowH = 52;

      // Card bg
      row.container.y = y;
      row.labelText.y = rowH / 2 - 7;

      const isOn = state.settings[row.key];
      const btnX = pad + rowW - toggleBtnW - 12;
      const btnY = (rowH - toggleBtnH) / 2;

      row.btnGfx.clear();
      drawPillButton(
        row.btnGfx, btnX, btnY, toggleBtnW, toggleBtnH,
        isOn ? THEME.button.primary : THEME.button.disabled
      );

      row.btnText.text = isOn ? t('on') : t('off');
      row.btnText.x = btnX + toggleBtnW / 2;
      row.btnText.y = btnY + toggleBtnH / 2;

      y += rowH;
    }

    y += 16;

    // ── Language selector ──
    this.langLabel.y = y;
    y += 28;

    this.langContainer.y = y;
    const chipH = 30;
    const chipGap = 6;
    const maxChipW = (rowW - chipGap * 2) / 3;
    let cx = pad;
    let ly = 0;

    for (const chip of this.langChips) {
      const label = LANGUAGE_LABELS[chip.lang] || chip.lang;
      const chipW = Math.min(Math.max(label.length * 9 + 16, 56), maxChipW);

      if (cx + chipW > this.panelW - pad) {
        cx = pad;
        ly += chipH + chipGap;
      }

      const isActive = state.settings.language === chip.lang;

      chip.gfx.clear();
      if (isActive) {
        chip.gfx.beginFill(THEME.accent.gold, 0.2);
        chip.gfx.lineStyle(1, THEME.accent.gold, 0.5);
      } else {
        chip.gfx.beginFill(THEME.bg.glass, THEME.bg.glassAlpha);
        chip.gfx.lineStyle(1, THEME.border.glass, THEME.border.glassAlpha);
      }
      chip.gfx.drawRoundedRect(cx, ly, chipW, chipH, chipH / 2);
      chip.gfx.endFill();
      chip.gfx.lineStyle(0);

      chip.text.x = cx + chipW / 2;
      chip.text.y = ly + chipH / 2;
      chip.text.style.fill = isActive ? THEME.text.gold : THEME.text.secondary;

      cx += chipW + chipGap;
    }

    y += ly + chipH + 24;

    // ── Reset button ──
    const resetBtnW = Math.min(rowW, 200);
    const resetBtnH = 40;
    const resetBtnX = (this.panelW - resetBtnW) / 2;

    this.resetGfx.clear();
    drawPillButton(this.resetGfx, resetBtnX, y, resetBtnW, resetBtnH, THEME.button.danger);
    this.resetText.x = resetBtnX + resetBtnW / 2;
    this.resetText.y = y + resetBtnH / 2;

    y += resetBtnH + 24;

    // ── Version ──
    this.versionText.x = this.panelW / 2;
    this.versionText.y = y;
  }

  onResize(w: number, h: number): void {
    this.panelW = w;
    this.panelH = h;
  }
}
